import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  Play, 
  Check, 
  Zap,
  RefreshCw,
  FileText,
  Users,
  Calendar,
  Trash2,
  RotateCcw
} from 'lucide-react';

const ProcessingDashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [staffNo, setStaffNo] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  // Background job tracking
  const [activeJob, setActiveJob] = useState(null); // { job_id, label }
  const pollRef = React.useRef(null);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!user?.is_admin) {
      setError('Admin privileges required to access this page');
      setLoading(false);
      return;
    }
    fetchDashboardData();
    fetchCompanies();
  }, [isAuthenticated, user, navigate]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/processing/companies', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setCompanies(await res.json());
    } catch { /* non-critical — selector stays empty */ }
  };

  // Poll active background job every 3 seconds until done or error
  useEffect(() => {
    if (!activeJob) return;

    const token = localStorage.getItem('access_token');
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/processing/job-status/${activeJob.job_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();

        if (data.status === 'done') {
          clearInterval(pollRef.current);
          setActiveJob(null);
          setActionLoading(false);
          setSuccessMessage(`${activeJob.label} completed successfully.`);
          fetchDashboardData();
        } else if (data.status === 'error') {
          clearInterval(pollRef.current);
          setActiveJob(null);
          setActionLoading(false);
          setError(`${activeJob.label} failed: ${data.message}`);
        }
      } catch {
        clearInterval(pollRef.current);
        setActiveJob(null);
        setActionLoading(false);
        setError('Lost contact with server while polling job status.');
      }
    }, 3000);

    return () => clearInterval(pollRef.current);
  }, [activeJob]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      console.log('Token from localStorage:', token); // Debug log
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const queryParams = new URLSearchParams({
        start_date: dateRange.startDate,
        end_date: dateRange.endDate
      });
      
      if (staffNo) {
        queryParams.append('staff_no', staffNo);
      }
      
      const response = await fetch(`/api/processing/dashboard?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('Error response:', errorText);
        throw new Error(`API Error ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('Dashboard data:', data);
      setDashboardData(data);
    } catch (error) {
      setError(`Failed to load dashboard data: ${error.message}`);
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = async (action, actionName) => {
    setActionLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(`/api/processing/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          staff_no: staffNo || undefined,
          company_id: selectedCompanyId ? parseInt(selectedCompanyId) : undefined
        })
      });

      const result = await response.json();

      if (response.status === 202 && result.job_id) {
        // Background job started — poll for completion
        setActiveJob({ job_id: result.job_id, label: actionName });
        setSuccessMessage(`${actionName} started in background…`);
        return; // keep actionLoading=true until poll completes
      }

      if (!response.ok) throw new Error(result.error || `Failed to ${actionName}`);

      setSuccessMessage(result.message);
      await fetchDashboardData();
    } catch (err) {
      setError(`Failed to ${actionName}: ${err.message}`);
    } finally {
      // Only clear spinner here for synchronous actions; async ones clear on poll completion
      if (!activeJob) setActionLoading(false);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm(`Are you sure you want to delete batch ${batchId?.substring(0, 8)}...? This will remove all staging and final records for this batch and reset the raw data for reprocessing.`)) {
      return;
    }
    
    setActionLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch(`/api/processing/delete-batch/${batchId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete batch');
      }

      const result = await response.json();
      setSuccessMessage(result.message);
      
      // Refresh dashboard data
      await fetchDashboardData();
    } catch (error) {
      setError(`Failed to delete batch: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetAllData = async () => {
    if (!window.confirm('WARNING: This will reset ALL transformed_at flags on raw records. Are you sure you want to continue?')) {
      return;
    }

    const clearStaging = window.confirm('Also clear all staging records?');
    const clearFinal = window.confirm('Also clear all final records?');

    setActionLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch('/api/processing/reset-all-raw-data', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ clear_staging: clearStaging, clear_final: clearFinal })
      });

      const result = await response.json();

      if (response.status === 202 && result.job_id) {
        setActiveJob({ job_id: result.job_id, label: 'Reset All Raw Data' });
        setSuccessMessage('Reset started in background…');
        return;
      }

      if (!response.ok) throw new Error(result.details || result.error || 'Failed to reset data');

      setSuccessMessage(result.message);
      await fetchDashboardData();
    } catch (err) {
      setError(`Failed to reset data: ${err.message}`);
      setActionLoading(false);
    }
  };

  const handleProcessAndApprove = async () => {
    await handleQuickAction('quick-action/process-and-approve', 'Process & Approve');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Attendance Processing Dashboard</h1>
              <p className="text-sm text-gray-500 mt-1">Manage and process attendance records</p>
            </div>
            <button
              onClick={fetchDashboardData}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            Filter Options
          </h3>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                Start Date
              </label>
              <input
                type="date"
                id="start_date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                End Date
              </label>
              <input
                type="date"
                id="end_date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label htmlFor="staff_no" className="block text-sm font-medium text-gray-700">
                Staff No (Optional)
              </label>
              <input
                type="text"
                id="staff_no"
                value={staffNo}
                onChange={(e) => setStaffNo(e.target.value)}
                placeholder="e.g. EMP001"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label htmlFor="company_id" className="block text-sm font-medium text-gray-700">
                Entity (Optional)
              </label>
              <select
                id="company_id"
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All entities</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={fetchDashboardData}
                disabled={loading}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Apply Filter
              </button>
            </div>
          </div>
          
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => setDateRange({
                startDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
                endDate: format(new Date(), 'yyyy-MM-dd')
              })}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
            >
              Today
            </button>
            <button
              onClick={() => setDateRange({
                startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
                endDate: format(new Date(), 'yyyy-MM-dd')
              })}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => setDateRange({
                startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
                endDate: format(new Date(), 'yyyy-MM-dd')
              })}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
            >
              Last 30 Days
            </button>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
          <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
          <span className="text-green-700 text-sm">{successMessage}</span>
        </div>
      )}

      {activeJob && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 flex-shrink-0" />
          <div>
            <p className="text-blue-800 text-sm font-medium">{activeJob.label} running in background…</p>
            <p className="text-blue-600 text-xs mt-0.5">This page will update automatically when complete.</p>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      {dashboardData && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {/* Pending Records */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Pending Records
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardData.pending_records}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Awaiting Approval */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Awaiting Approval
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardData.processed_awaiting_approval}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Exceptions */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Exceptions
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardData.exceptions_requiring_attention}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Batches */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Recent Batches
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardData.recent_batches?.length || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {dashboardData && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Quick Actions
            </h3>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Process All Pending */}
              <button
                onClick={() => handleQuickAction('process-all-pending', 'process pending records')}
                disabled={actionLoading || !dashboardData.quick_actions_available.process_all_pending}
                className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Process Pending
              </button>

              {/* Approve All Processed */}
              <button
                onClick={() => handleQuickAction('approve-all-processed', 'approve processed records')}
                disabled={actionLoading || !dashboardData.quick_actions_available.approve_all_processed}
                className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Approve All
              </button>

              {/* Process & Approve */}
              <button
                onClick={handleProcessAndApprove}
                disabled={actionLoading || (!dashboardData.quick_actions_available.process_all_pending && !dashboardData.quick_actions_available.approve_all_processed)}
                className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Zap className="h-4 w-4 mr-2" />
                )}
                Process & Approve
              </button>

              {/* View Exceptions */}
              <button
                disabled={!dashboardData.quick_actions_available.review_exceptions}
                className="inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                View Exceptions
              </button>
            </div>
            
            {/* Development Tools - Only for testing/reprocessing */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-500 mb-3">
                Development Tools (For Testing/Reprocessing)
              </h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Reset All Raw Data */}
                <button
                  onClick={handleResetAllData}
                  disabled={actionLoading}
                  className="inline-flex items-center justify-center px-4 py-3 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                  ) : (
                    <RotateCcw className="h-4 w-4 mr-2" />
                  )}
                  Reset All Raw Data
                </button>

                {/* Clear Exceptions */}
                <button
                  onClick={() => handleQuickAction('quick-action/clear-exceptions', 'clear exceptions')}
                  disabled={actionLoading}
                  className="inline-flex items-center justify-center px-4 py-3 border border-yellow-300 text-sm font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-2"></div>
                  ) : (
                    <AlertTriangle className="h-4 w-4 mr-2" />
                  )}
                  Clear Exceptions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Batches Table */}
      {dashboardData && dashboardData.recent_batches && dashboardData.recent_batches.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Recent Batches
            </h3>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Batch ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Records
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uploaded
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboardData.recent_batches.map((batch, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {batch.batch_id?.substring(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {batch.total_records}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          batch.status === 'processed' ? 'bg-green-100 text-green-800' :
                          batch.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          batch.status === 'error' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {batch.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(batch.uploaded_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => handleDeleteBatch(batch.batch_id)}
                          disabled={actionLoading}
                          className="inline-flex items-center px-2 py-1 border border-red-300 text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          title="Delete batch for reprocessing"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessingDashboard;
